import i18n from '../../../i18n';
import { prepareLayer, getAdjustedCenterOn } from '../../../services/map';
import { isSmallDevice } from '../../../services/utilities/devices';
import { getViewPadding } from '../../../services/utilities/utilities';
import { getMobileBoundingBox } from '../../../services/map/boundingBox';
import OlVectorSource from 'ol/source/Vector';
import OlOverlay from 'ol/Overlay';
import {
    MAP_POPUP_FADEOUT_MS,
    PROJECTION_CODE,
    DEFAULT_MIN_ZOOM_RESOLUTION,
    DEFAULT_ANIMATION_MS,
    API_TYPE,
    INTERFACE_PANEL_TRIGGERS,
    PANEL_WIDTHS,
    NEDGLOBE_VIEWER,
    NG_LAYER_TYPES
} from '../../../constants';

export const actions = {
    async addLayerToMap(store, { mapName, id, highQuality }) {

        function setOpacity(store, id, olLayer, layerObject) {
            // Get opacity from usermap
            const currentUserMap = store.getters.getActiveUserMap;
            const sortedLayer = currentUserMap?.content?.sortedLayers?.find(s => s.id === id);

            // if usermap has opacity setting use that
            if (sortedLayer?.opacity) {
                olLayer.setOpacity(1 - Number(sortedLayer?.opacity));

                // Otherwise if layer has style and transparency setting > 0 then set opacity of the layer
            } else if (layerObject.data.styles && layerObject.data.styles[0].transparency > 0) {
                const opacity = 1 - (Number(layerObject.data.styles[0].transparency) / 100);
                olLayer.setOpacity(opacity);
            }

        };

        const mapConfig = store.getters.getMapByName(mapName);
        if (!mapConfig) {
            throw new Error('map not found');
        }
        const map = mapConfig.map;
        const layerObject = store.getters.getLayerById(id);
        if (!layerObject) {
            throw new Error('layer not found');
        }

        // only add checked theme layers
        // Do not test for web
        if (!store.getters.isWeb && layerObject.type === NG_LAYER_TYPES.THEME && !layerObject.checked) {
            return;
        }

        try {
            const olLayer = await prepareLayer(store, map, layerObject, highQuality, layerObject.extended.alwaysCreateWfsLayer);
            const maxResolution = layerObject.extended.maxResolution;
            const minResolution = layerObject.extended.minResolution;
            const textMaxResolution = layerObject.extended.textMaxResolution;

            if (!olLayer) {
                throw new Error('Layer could not be created');
            }
            // BaseLayers zitten op zIndex op 0
            if (layerObject.type === 'base') {
                olLayer.setZIndex(NEDGLOBE_VIEWER.mapLayerZIndexes.base);
                store.commit('SET_BASE_LAYER_TO_MAP', { mapName, id, olLayer, terms: layerObject.data.mapTerms });
            } else {

                const defaultIndex = await store.dispatch('getDefaultLayerZindex', { id });

                let userMapIndex = null;
                const userOrderedThemeLayerList = store.getters.getActiveUserMap?.content?.sortedLayers;

                if (layerObject.type === 'theme') {
                    userMapIndex = userOrderedThemeLayerList?.find(theme => theme.id === id && theme.type === 'theme')?.order;
                }
                if (layerObject.type === 'feedback') {
                    userMapIndex = userOrderedThemeLayerList?.find(theme => theme.feedbackId === layerObject.data.feedbackId && theme.type === 'feedback')?.order;
                }

                let zIndex = userMapIndex ?? defaultIndex;

                // Index must be unique so make a list of z-indexes in use
                const usedZIndexes = new Set(
                    mapConfig.layers.map(layer => layer.olLayer.getZIndex())
                );

                // As long as index is in use keep adding to it.
                while (usedZIndexes.has(zIndex)) {
                    zIndex++;
                }
                olLayer.setZIndex(zIndex);

                setOpacity(store, id, olLayer, layerObject);

                store.dispatch('configureClusterLayer', { mapConfig, olLayer, layerObject });
                store.commit('ADD_LAYER_TO_MAP', { mapName, id, olLayer, minResolution, maxResolution, terms: layerObject.data.mapTerms });
                store.commit('DISPLAY_LAYER', { mapName, id });
                await store.dispatch('createExtraWfsLayer', { mapName, map, layerObject, id, highQuality, minResolution, maxResolution, textMaxResolution, zIndex });
            }
        } catch (err) {
            throw new Error(err);
        }
    },

    getDefaultLayerZindex(store, { id }) {
        const layerObject = store.getters.getLayerById(id);
        // Base layers have z-index 0
        if (layerObject.type === 'base') {
            return NEDGLOBE_VIEWER.mapLayerZIndexes.base;
        }
        // Get the index for the layer in the theme list
        const defaultIndex = [...store.getters.getAllOrderedLayersIds].reverse().findIndex(layerId => layerId === id);

        // We add a default index to the defaultIndex based on the type of theme/layer
        if (layerObject.type === 'theme') {
            const theme = store.getters.getThemeById(layerObject.data.themeId);

            // Non covering themelayers are displayed on top of covering layers. So we start
            // non-covering layers at theme level
            if (!theme?.covering) {
                return defaultIndex + NEDGLOBE_VIEWER.mapLayerZIndexes.theme;
            } else {
                // covering layers start at level covering theme
                return NEDGLOBE_VIEWER.mapLayerZIndexes.coveringTheme;
            }
        }
        // feedback layers are default always on top so we start these at feedback level
        if (layerObject.type === 'feedback') {
            return defaultIndex + NEDGLOBE_VIEWER.mapLayerZIndexes.feedback;
        }

        return 0;

    },

    async createExtraWfsLayer(store, { mapName, map, layerObject, id, highQuality, minResolution, maxResolution, textMaxResolution, zIndex }) {
        let olLayerWithStyle = null;
        // if layer with a graphical layer has a textattribute style, then add an extra wfs layer
        if (layerObject.extended.hasGraphicalLayerAndStyleTextAttribute) {
            olLayerWithStyle = await prepareLayer(store, map, layerObject, highQuality, true);
        }
        if (olLayerWithStyle) {
            const currentStyle = layerObject.extended?.computedStyles[0]?.settings[NEDGLOBE_VIEWER.styleDisplayTypes.default.toLowerCase()];
            if (currentStyle) {
                currentStyle.fillColor = 'transparent';
                currentStyle.strokeColor = 'transparent';
                currentStyle.icon = null;
            }
            olLayerWithStyle.setZIndex(zIndex);
            // use maxResolution for texts
            if (textMaxResolution) {
                maxResolution = textMaxResolution;
            }
            store.commit('ADD_LAYER_TO_MAP', { mapName, id: `${id}_style`, olLayer: olLayerWithStyle, minResolution, maxResolution, terms: layerObject.data.mapTerms });
            store.commit('DISPLAY_LAYER', { mapName, id: `${id}_style` });
        }
    },
    configureClusterLayer(store, { mapConfig, olLayer, layerObject }) {
        // clusters on the main map will go to detail mode depending on maxResolutionCluster
        if (mapConfig.name === 'main' && layerObject.extended.useCluster) {
            const clusterSource = olLayer.getSource();
            mapConfig.map.getView().on('change:resolution', () => {
                if (mapConfig.viewport.resolution <= layerObject.extended.maxResolutionCluster) {
                    clusterSource.setDistance(0);
                } else {
                    clusterSource.setDistance(100);
                }
            });
        }
    },
    updateLayerOnMap({ dispatch }, { mapName, id }) {
        dispatch('addLayerToMap', { mapName, id });
    },
    removeLayerFromMap({ commit }, { mapName, id }) {
        commit('HIDE_LAYER', { mapName, id });
        commit('CLEAR_FILTER', { layerId: id });
    },
    // eslint-disable-next-line complexity
    zoomToFeatures({ state, getters }, { mapName, features, options, zoomWhenOutOfView, minResolution = DEFAULT_MIN_ZOOM_RESOLUTION }) {
        mapName = mapName || state.defaultMapName;

        // default margin takes panels into account.
        const usePanelMargin = true;
        const infoTablePanel = state.uiElements[INTERFACE_PANEL_TRIGGERS.INFORMATION];
        const infoTableHeight = infoTablePanel.visible ? infoTablePanel.height : 0;
        const padding = getViewPadding(infoTableHeight, usePanelMargin);

        const fitOptions = { padding, duration: DEFAULT_ANIMATION_MS, ...options, minResolution };
        const mapConfig = state.maps.find(_map => _map.name === mapName);

        if (!mapConfig) {
            throw new Error('map not found');
        }
        if (!features?.length > 0) {
            throw new Error('no features to zoom to');
        }
        const view = mapConfig.map.getView();
        const vectorSource = new OlVectorSource({
            projection: PROJECTION_CODE,
            features: features //add an array of features
        });

        if (zoomWhenOutOfView) {
            // Don't zoom when small device
            if (isSmallDevice()) {
                return;
            }

            // Check if feature is not behind ADVANCED_SEARCH_PANEL
            let isOutsideSearchPanel = true;
            if (state.uiElements[INTERFACE_PANEL_TRIGGERS.ADVANCED_SEARCH].visible) {
                const height = getters.getAdvancedSearchPanelHeight;
                const width = getters.getAdvancedSearchPanelWidth;
                const lowerLeft = mapConfig.map.getCoordinateFromPixel([0, height]);
                const upperRight = mapConfig.map.getCoordinateFromPixel([width, 0]);
                const extent = [lowerLeft[0], lowerLeft[1], upperRight[0], upperRight[1]];
                if (vectorSource.getFeaturesInExtent(extent).length > 0) {
                    isOutsideSearchPanel = false;
                }
            }

            // If table visible then either mobile or web with table view.
            const table = state.uiElements[INTERFACE_PANEL_TRIGGERS.INFORMATION].visible;
            if (table) {
                // Move selection when behind information panel.
                // Calculate extent based on screensize minus informationpanel height pixels at the bottom
                // -40 for blue box height
                const mapsize = mapConfig.map.getSize();

                // panel width is when maxwidth, the map width, otherwise default panel width of half screen width
                const panelWidth = state.uiElements[INTERFACE_PANEL_TRIGGERS.INFORMATION].maxWidth ? mapsize[0] : mapsize[0] / 2;

                // panel height = panelheight + 40 (=blue box height).
                const panelHeight = getters.getDetailInformationPanelHeight + 40;

                // Create an extent for the informationpanel
                const lowerLeft = mapConfig.map.getCoordinateFromPixel([mapsize[0] - panelWidth, mapsize[1]]);
                const upperRight = mapConfig.map.getCoordinateFromPixel([mapsize[0], mapsize[1] - panelHeight]);
                const extent = [lowerLeft[0], lowerLeft[1], upperRight[0], upperRight[1]];
                const isOutsideInfoPanel = vectorSource.getFeaturesInExtent(extent).length === 0;

                // When all selected items are outside the informationpanel/searchpanel there's no need to zoom
                if (isOutsideInfoPanel && isOutsideSearchPanel) {
                    return;
                }
            } else {
                // Web panel
                // Move selection when behind information panel.
                // Calculate extent based on screensize minus PANEL_WIDTH.WEB_INFORMATION pixel panel width on the right hand side
                const mapsize = mapConfig.map.getSize();
                const lowerLeft = mapConfig.map.getCoordinateFromPixel([0, mapsize[1]]);
                const upperRight = mapConfig.map.getCoordinateFromPixel([mapsize[0] - PANEL_WIDTHS.WEB_INFORMATION, 0]);
                const extent = [lowerLeft[0], lowerLeft[1], upperRight[0], upperRight[1]];
                if (vectorSource.getFeaturesInExtent(extent).length > 0) {
                    // not behind informationpanel, but maybe behind advanced search panel
                    if (state.uiElements[INTERFACE_PANEL_TRIGGERS.ADVANCED_SEARCH].visible) {
                        // When all selected items are outside the searchpanel there's no need to zoom
                        if (isOutsideSearchPanel) {
                            return;
                        }
                    }
                } else {
                    // Check if feature is below WEB_INFORMATION_PANEL
                    const height = getters.getWebInformationPanelHeight;
                    const lowerLeft = mapConfig.map.getCoordinateFromPixel([mapsize[0] - PANEL_WIDTHS.WEB_INFORMATION, mapsize[1]]);
                    const upperRight = mapConfig.map.getCoordinateFromPixel([mapsize[0], height]);
                    const extent = [lowerLeft[0], lowerLeft[1], upperRight[0], upperRight[1]];
                    if (vectorSource.getFeaturesInExtent(extent).length > 0) {
                        return;
                    }
                }
            }
        }
        const dataExtent = vectorSource.getExtent();
        view.fit(dataExtent, fitOptions);
    },
    zoomToPoint({ state, getters }, { mapName, point, options, minResolution = DEFAULT_MIN_ZOOM_RESOLUTION }) {
        // Given a point, make sure it falls in visible part of screen
        // and is not hidden behind selection tab panel.

        // Don't zoom when small device
        if (isSmallDevice()) {
            return;
        }
        mapName = mapName || state.defaultMapName;
        const mapConfig = state.maps.find(_map => _map.name === mapName);

        if (!mapConfig) {
            throw new Error('map not found');
        }

        // If table visible then we are in either in mobile or in web with table view.
        const table = state.uiElements[INTERFACE_PANEL_TRIGGERS.INFORMATION].visible;

        // calculate top XY of panel.
        const mapsize = mapConfig.map.getSize();
        let panelX;
        let panelY;

        if (table) {
            // Move selection when behind information panel.
            // Calculate extent based on screensize minus informationpanel height pixels at the bottom
            // -40 for blue box height
            // panel width is when maxwidth, the map width, otherwise default panel width of half screen width
            const panelWidth = state.uiElements[INTERFACE_PANEL_TRIGGERS.INFORMATION].maxWidth ? mapsize[0] : mapsize[0] / 2;

            // panel height = panelheight + 40 (=blue box height).
            const panelHeight = getters.getDetailInformationPanelHeight + 40;
            panelX = mapsize[0] - panelWidth;
            panelY = mapsize[1] - panelHeight;
        } else {
            // Web panel
            // Move selection when behind information panel.
            // Calculate extent based on screensize minus PANEL_WIDTHS.WEB_INFORMATION pixel panel width on the right hand side
            panelY = 0;
            panelX = mapsize[0] - PANEL_WIDTHS.WEB_INFORMATION;
        }

        // Get pixel coordinates of selected point.
        const pointXY = mapConfig.map.getPixelFromCoordinate(point);

        // If point XY greater than panel XY we need to zoom point into view.
        if (pointXY[0] > panelX && pointXY[1] > panelY) {
            const view = mapConfig.map.getView();
            const dataExtent = [point[0], point[1], point[0], point[1]];
            // default margin takes panels into account.
            const padding = getViewPadding(true);
            const fitOptions = { padding, duration: DEFAULT_ANIMATION_MS, ...options, minResolution };
            view.fit(dataExtent, fitOptions);
        }
    },
    zoomToExtent({ state, getters }, mapName) {
        mapName = mapName || state.defaultMapName;
        const mapConfig = getters.getMapByName(mapName);
        if (!mapConfig) {
            throw new Error('map not found');
        }
        let orgExtent;
        const map = mapConfig.map;
        const user = getters.getUserData;
        orgExtent = getMobileBoundingBox(user);
        const size = map.getSize();

        if (orgExtent && size) {
            const fitOptions = {
                size,
                duration: DEFAULT_ANIMATION_MS
            };
            map.getView().fit(orgExtent, fitOptions);
        }
    },
    customFocus({ dispatch, getters }, { mapName, resolution, point, x = 'center', y = 'center' }) {
        const mapConfig = getters.getMapByName(mapName);
        const xfactors = {
            left: 1,
            center: 2,
            right: 3
        };
        const yfactors = {
            top: 1,
            center: 2,
            bottom: 3
        };
        if (!mapConfig) {
            throw new Error('map not found');
        }
        const map = mapConfig.map;
        const view = map.getView();
        const orgResolution = view.getResolution();
        const xFactor = xfactors[x];
        const yFactor = yfactors[y];
        if (xFactor && yFactor) {
            const mapSize = map.getSize();
            const dX = mapSize[0] * (xFactor / 4);
            const dY = mapSize[1] * (yFactor / 4);
            const center = getAdjustedCenterOn(point, mapSize, [dX, dY], view, resolution || orgResolution);
            return dispatch('updateViewPortMap', { mapName, resolution, center, animated: 'true' });
        }
        return null;
    },
    updateViewPortMap({ commit, getters }, { mapName, zoom, center, rotation, animated, resolution, duration = 500 }) {
        const mapConfig = getters.getMapByName(mapName);
        if (!mapConfig) {
            throw new Error('map not found');
        }
        const map = mapConfig.map;
        const view = map.getView();
        if (animated && center) {
            view.animate(
                {
                    center,
                    resolution,
                    duration
                },
                () => commit('MAP_MOVE_END', { mapName })
            );
        } else {
            if (zoom) {
                view.setZoom(zoom);
            }
            if (center) {
                view.setCenter(center);
            }
            if (rotation !== undefined && rotation !== null) {
                view.setRotation(rotation);
            }
            if (resolution) {
                view.setResolution(resolution);
            }
            commit('MAP_MOVE_END', { mapName });
        }
    },
    async getAddressInfo({ dispatch, state }, point) {
        return dispatch('useApiData', {
            name: 'WebGetAddressInfo',
            parameters: { location: point.join(',') },
            endpoint: state.appConfig[API_TYPE.WEBAPI].endpoint
        });
    },
    // V1 version of locationservice.
    // The V0 version is kept in place for calls from feedback functions.
    async getAddressInfoV1({ dispatch }, point) {
        try {
            const result = await dispatch('useApiData', {
                name: 'GetAddressAtLocation',
                parameters: { Location: point.join(',') }
            });
            return result.data;
        } catch {
            return '';
        }
    },
    async getAddressesAtLocation({ commit, dispatch }, point) {
        try {
            const result = await dispatch('useApiData', {
                name: 'GetAddressesAtLocation',
                parameters: { Location: point.join(',') }
            });
            return result.data;
        } catch (err) {
            commit('ADD_STATUS_MESSAGE', {
                message: i18n.t('errorPdokNotAvailable'),
                variant: 'danger',
                autoHideDelay: 5000
            });
            return '';
        }
    },
    mapMoveEnd({ commit }, { mapName }) {
        commit('MAP_MOVE_END', { mapName });
    },
    mapPointerdrag({ commit }, { mapName }) {
        commit('MAP_POINTER_DRAG', { mapName });
    },
    mapClicked({ commit }, { mapName, event }) {
        commit('MAP_CLICKED', { mapName, coordinate: event.coordinate, event });
    },
    mapDoubleClicked({ commit }, { mapName, event }) {
        commit('MAP_DOUBLE_CLICKED', { mapName, coordinate: event.coordinate, event });
    },
    mapResolutionChanged({ commit, getters }, { mapName }) {
        const mapConfig = getters.getMapByName(mapName);
        const view = mapConfig.map.getView();
        const zoom = view.getZoom();
        const roundedDownMapZoom = parseInt(zoom, 10);
        if (mapConfig.zoom !== roundedDownMapZoom) {
            commit('MAP_ZOOM_CHANGE', { mapName, zoom });
        }
    },
    mapStartInteraction({ commit }, { mapName, event }) {
        commit('MAP_START_INTERACTION', { mapName, coordinate: event.coordinate });
    },
    mapPointerDownShort({ commit }, { mapName, event }) {
        commit('MAP_POINTER_DOWN_SHORT', { mapName, coordinate: event.coordinate, button: event.originalEvent.button, event });
    },
    mapPointerDownLong({ commit }, { mapName, event }) {
        commit('MAP_POINTER_DOWN_LONG', { mapName, coordinate: event.coordinate, button: event.originalEvent.button, event });
    },
    mapPointerUpShort({ commit }, { mapName, event }) {
        commit('MAP_POINTER_UP_SHORT', { mapName, coordinate: event.coordinate, button: event.originalEvent.button, event });
    },
    mapPointerUpLong({ commit }, { mapName, event }) {
        commit('MAP_POINTER_UP_LONG', { mapName, coordinate: event.coordinate, button: event.originalEvent.button, event });
    },
    showMapPopup({ getters }, { mapName, className, message, position, offset }) {
        const mapConfig = getters.getMapByName(mapName);
        const map = mapConfig.map;

        const element = document.createElement('div');
        element.className = className;

        const spanElement = document.createElement('span');
        spanElement.innerText = message;
        element.appendChild(spanElement);

        const overlay = new OlOverlay({ element });
        overlay.setPosition(position);
        overlay.setOffset([0, offset ? offset : 0]);
        map.addOverlay(overlay);

        setTimeout(() => {
            element.classList.add('fadeout');
        }, MAP_POPUP_FADEOUT_MS);

        setTimeout(() => {
            map.removeOverlay(overlay);
        }, MAP_POPUP_FADEOUT_MS + 2000);
    },
    zoomToFeature({ state, dispatch }, { mapName, feature, usePanelMargin = true }) {
        const padding = getViewPadding(usePanelMargin);

        dispatch('zoomToFeatures', {
            mapName,
            features: feature,
            zoomWhenOutOfView: false,
            options: {
                padding,
                duration: DEFAULT_ANIMATION_MS
            }
        });
    },
    async getScales({ commit, dispatch }) {
        const loadedScales = await dispatch('useApiData', { name: 'GetMapScalesMobile' });

        commit('SET_SCALES', { scales: JSON.parse(loadedScales.data) });
    },
    async toggle3DLayer({ state, commit, getters }, { layer3D }) {
        const ol3DMap = state.map3D;
        const scene = ol3DMap.getCesiumScene();
        const primitive = scene.primitives.get(layer3D.index);

        // if layer is active, show it if already added or create the tileset and add to scene.primitives
        if (layer3D.active) {
            if (primitive) {
                primitive.show = true;
            } else {
                window.Cesium.Cesium3DTileset.fromUrl(
                    layer3D.url
                ).then(tileset => {
                    // Store index in layer state so we can find
                    // the tilesets in the scene.primitives when we have to switch off the layer.
                    commit('SET_LAYER_3D', { layer3D, tileset, index: scene.primitives.length });

                    scene.primitives.add(tileset);
                    scene.camera.setView({
                        orientation: {
                            heading: window.Cesium.Math.toRadians(0.0),
                            pitch: window.Cesium.Math.toRadians(-45.0),
                            roll: 0.0,
                        },
                    });
                    scene.sun = new window.Cesium.Sun();
                    scene.shadowMap.enabled = false;
                });
            }
            ol3DMap.setEnabled(true);
        } else {
            if (primitive) {
                primitive.show = false;
            }

            // Store active state of the layer
            commit('SET_LAYER_3D', { layer3D, index: layer3D.index });

            // When none of the layers are active, we can then disable the ol3DMap and hide the 3D window
            // Check on getMap3DEnabled is necessary to avoid that an error is thrown when 3D window was closed by the user
            const activeLayers3D = getters.getLayers3D.filter(({ active }) => active);
            if (activeLayers3D.length === 0 && getters.getMap3DEnabled) {
                ol3DMap.setEnabled(false);
                commit('SET_MAP3D_ENABLED', { enabled: false });
            }
        }
    }
};
